import {useQuery, useInfiniteQuery, useQueryClient, useMutation} from "react-query";
import {toast} from 'react-toastify';
import API from './utils/api';
import {
    EmployeeRelativeDto, EmployeeRelativePaginator,
    CreateEmployeeRelativeDto, QueryEmployeeRelativeDto,
    UpdateEmployeeRelativeDto
} from "../models/employee-relative.dto";
import ENDPOINTS from "./utils/endpoints";
import {QueryOptionsDto} from "../models/generic.dto";

export function useGetEmployeeRelative(options?: QueryEmployeeRelativeDto) {
    const url = ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_RELATIVE.GET;
    const request = (options: QueryEmployeeRelativeDto) => API.ProcessingCompanyAPI.getEmployeeRelative(options);

    const {
        data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        hasPreviousPage,
        fetchPreviousPage,
    } = useInfiniteQuery<EmployeeRelativePaginator, Error>(
        [url, options],
        ({ queryKey, pageParam }) => request(Object.assign({}, queryKey[1], pageParam)),
        {...API.Handlers.pageParamOption}
    );

    return API.Handlers.paginationHandler(data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        hasPreviousPage,
        fetchPreviousPage)
}

export function useGetEmployeeRelativeById(id: number) {
    const url = ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_RELATIVE.DETAIL;
    const request = API.ProcessingCompanyAPI.getEmployeeRelativeById(id);

    const {data, isLoading, error} = useQuery<{ data:EmployeeRelativeDto }, Error>(
        [url, id],
        (() => request),
        {
            enabled: !!(id),
        }
    );

    return {
        data: data,
        isLoading,
        error,
    };
}
export function useCreateEmployeeRelative() {
    const queryClient = useQueryClient();

    return useMutation(
        ({payload}: {payload: CreateEmployeeRelativeDto}) => API.ProcessingCompanyAPI.createEmployeeRelative(payload),
        {
            onSuccess: () => {
                toast.success('Record created successfully');
                queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_RELATIVE.GET);
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}

export function useUpdateEmployeeRelative() {
    const queryClient = useQueryClient();

    return useMutation(
        ({payload, id}: {payload: UpdateEmployeeRelativeDto, id: number}) => API.ProcessingCompanyAPI.updateEmployeeRelative(id, payload),
        {
            onSuccess: () => {
                toast.success('Record updated successfully');
                queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_RELATIVE.GET);
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}

export function useDeleteEmployeeRelative() {
    const queryClient = useQueryClient();

    return useMutation(
        ({ id}: {id: number}) => API.ProcessingCompanyAPI.deleteEmployeeRelative(id),
        {
            onSuccess: () => {
                toast.success('Record deleted successfully');
                queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.EMPLOYEE_RELATIVE.GET);
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}